/**
 * Draws the leaderboard display: the three screens (ranking pages, podium) for the venue TV and for a player's phone.
 * The HTML builders are pure and tested; `mountTvDisplay` and `renderWebBoard` add the behaviour (rotation, swipe, refresh).
 * What the screens say and which ones exist is decided in ../leaderboardDisplay.ts. The design is docs/mockups/.
 */

import { readBranding } from '../branding';
import { esc } from '../helpers';
import { fetchTvBoard, type TvReply } from '../leaderboardClient';
import {
  displayScreens,
  footerLeft,
  footerRight,
  formatPoints,
  gapText,
  seasonLine,
  stripText,
  PAGE_SIZE,
  type DisplayBoard,
  type DisplayBranding,
  type DisplayScreen,
  type DisplaySettings,
} from '../leaderboardDisplay';
import { applyTheme } from '../theme';
import { updateMarker } from '../updateSafety';

export type DisplayView = 'tv' | 'web';

export interface DisplayModel {
  view: DisplayView;
  board: DisplayBoard | null;
  branding: DisplayBranding;
  settings: DisplaySettings;
  generatedAt: number;
  now: number;
  /** Players to mark as "you" on the phone (the four who just played). */
  highlight?: string[];
}

const MEDALS: Record<number, [string, string, string]> = {
  1: ['#fbe08a', '#e0a516', '#a87400'],
  2: ['#f1f4f7', '#b4bfca', '#7b8794'],
  3: ['#f0b98a', '#c9763a', '#8a4a1c'],
};

export function medalSvg(rank: number, width: number): string {
  const c = MEDALS[rank];
  if (!c) return '';
  const id = `lbdMedal${rank}`;
  return `<svg width="${width}" height="${Math.round(width * 1.25)}" viewBox="0 0 64 80" aria-hidden="true">
    <defs><linearGradient id="${id}" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="${c[0]}"/><stop offset="1" stop-color="${c[1]}"/></linearGradient></defs>
    <path d="M14 0h14l8 26H22z" fill="var(--accent)"/><path d="M50 0H36l-8 26h14z" fill="var(--accent-dark)"/>
    <circle cx="32" cy="50" r="27" fill="url(#${id})" stroke="${c[2]}" stroke-width="3"/>
    <circle cx="32" cy="50" r="20" fill="none" stroke="${c[2]}" stroke-opacity=".45" stroke-width="2"/>
    <text x="32" y="59" text-anchor="middle" font-family="Baloo 2,system-ui" font-weight="800" font-size="26" fill="${c[2]}">${rank}</text></svg>`;
}

export function trophySvg(width: number): string {
  return `<svg class="lbd-trophy" width="${width}" height="${Math.round(width * 1.1)}" viewBox="0 0 64 70" aria-hidden="true">
    <path d="M16 14H6c0 13 5 19 13 21M48 14h10c0 13-5 19-13 21" fill="none" stroke="#7a5200" stroke-width="5" stroke-linecap="round"/>
    <path d="M16 4h32v20c0 13-7 21-16 21S16 37 16 24z" fill="#fbe08a" stroke="#7a5200" stroke-width="3.5" stroke-linejoin="round"/>
    <path d="M23 10v14c0 7 3 12 7 15" fill="none" stroke="#fff6c8" stroke-width="3.5" stroke-linecap="round"/>
    <path d="m32 15 2.6 5.3 5.8.8-4.2 4.1 1 5.8L32 28.2l-5.2 2.8 1-5.8-4.2-4.1 5.8-.8z" fill="#e0a516"/>
    <rect x="28" y="45" width="8" height="11" fill="#e0a516" stroke="#7a5200" stroke-width="3"/>
    <rect x="17" y="56" width="30" height="10" rx="4" fill="#fbe08a" stroke="#7a5200" stroke-width="3.5"/></svg>`;
}

function headerHtml(m: DisplayModel): string {
  const { logoDataUrl, name } = m.branding;
  const logoClass = logoDataUrl ? ` lbd-img${m.settings.logoPlate ? ' lbd-plate' : ''}` : '';
  const logo = logoDataUrl ? `<img src="${esc(logoDataUrl)}" alt="">` : '🀄';
  const sub = m.board ? seasonLine(m.board, m.now) : '';
  return `<div class="lbd-hdr"><div class="lbd-brand"><div class="lbd-logo${logoClass}">${logo}</div><div class="lbd-venue">${esc(name)}</div></div>
    <div class="lbd-right"><div class="lbd-title">Leaderboard</div><div class="lbd-sub">${esc(sub)}</div></div></div>`;
}

function stripHtml(m: DisplayModel): string {
  if (!m.board) return '';
  const s = stripText(m.board, m.view === 'web');
  const dot = m.board.state === 'live' ? '<i>●</i> ' : '';
  return `<div class="lbd-strip lbd-st-${m.board.state}"><span class="lbd-tag">${dot}${esc(s.tag)}</span><span class="lbd-stext">${esc(s.text)}${s.more ? `<span class="lbd-more">${esc(s.more)}</span>` : ''}</span></div>`;
}

/** The footer's left side: the green "live" dot while the season runs, and when the board was last refreshed. */
function liveHtml(board: DisplayBoard, generatedAt: number): string {
  return `${board.state === 'live' ? '<i>●</i> ' : ''}${esc(footerLeft(board, generatedAt))}`;
}

function rowsHtml(m: DisplayModel, items: DisplayBoard['entries'], startNumber: number): string {
  const mine = new Set((m.highlight ?? []).map((n) => n.trim().toLowerCase()));
  const size = m.view === 'tv' ? 30 : 22;
  return (
    `<div><div class="lbd-colhead lbd-g4"><span>#</span><span>Player</span><span>Wins</span><span>Points</span></div>` +
    items
      .map((e, i) => {
        const top = e.rank <= 3;
        const classes = ['lbd-row', 'lbd-g4', top ? 'lbd-top' : '', mine.has(e.nickname.toLowerCase()) ? 'lbd-mine' : ''].filter(Boolean).join(' ');
        return `<div class="${classes}" data-pos="${startNumber + i}"><span class="lbd-rk">${top ? medalSvg(e.rank, size) : e.rank}</span><span class="lbd-nm">${esc(e.nickname)}</span><span class="lbd-wn">${e.wins}</span><span class="lbd-pt">${formatPoints(e.points)}</span></div>`;
      })
      .join('') +
    '</div>'
  );
}

function tableBody(m: DisplayModel, page: number): string {
  const all = m.board!.entries;
  const start = page * PAGE_SIZE;
  const list = all.slice(start, start + PAGE_SIZE);
  if (list.length === 0) return '<p class="lbd-empty">No games have been counted yet.</p>';
  // On the TV a long page splits into two columns so all 25 fit without scrolling.
  const split = m.view === 'tv' && list.length > 13;
  const half = Math.ceil(list.length / 2);
  const cols = split
    ? `<div class="lbd-cols lbd-two">${rowsHtml(m, list.slice(0, half), start)}${rowsHtml(m, list.slice(half), start + half)}</div>`
    : `<div class="lbd-cols lbd-one">${rowsHtml(m, list, start)}</div>`;
  return `<div class="lbd-body">${cols}</div>`;
}

function podiumBody(m: DisplayModel): string {
  const top = m.board!.entries.slice(0, 3);
  const tv = m.view === 'tv';
  const state = m.board!.state;
  const colours = ['var(--accent)', '#f4c430', '#e87c8a', '#6ec3d8', 'var(--accent-dark)'];
  let deco = '';
  for (let i = 0; i < 16; i++) {
    deco += `<span class="lbd-conf" style="left:${(i * 37 + 8) % 100}%;background:${colours[i % 5]};animation-duration:${7 + (i % 5)}s;animation-delay:${-(i * 1.3)}s"></span>`;
  }
  for (let i = 0; i < 9; i++) {
    deco += `<span class="lbd-spark" style="left:${(i * 29 + 6) % 94}%;top:${(i * 23 + 10) % 55}%;font-size:${tv ? 34 : 18}px;animation-delay:${i * 0.45}s">✦</span>`;
  }
  const col = (position: number): string => {
    const e = top[position - 1]!;
    // A provisional podium has no trophy: nobody is the champion until staff mark the season final.
    const foot = position === 1 && state !== 'review' ? trophySvg(tv ? 96 : 40) : `<div class="lbd-numeral">${position}</div>`;
    return `<div class="lbd-pcol lbd-c${position}"><div class="lbd-medalwrap">${medalSvg(position, tv ? 96 : 46)}</div>
      <div class="lbd-block lbd-p${position}"><div class="lbd-bn">${esc(e.nickname)}</div><div class="lbd-bp">${formatPoints(e.points)}</div><div class="lbd-bw">${e.wins} round wins</div>${foot}</div></div>`;
  };
  const mine = new Set((m.highlight ?? []).map((n) => n.trim().toLowerCase()));
  const rows = top
    .map(
      (e, i) =>
        `<div class="lbd-trow lbd-r${i + 1}${mine.has(e.nickname.toLowerCase()) ? ' lbd-mine' : ''}">${medalSvg(i + 1, tv ? 46 : 28)}<div><div class="lbd-nm">${esc(e.nickname)}</div><div class="lbd-gp">${esc(gapText(m.board!.entries, i))}</div></div><div class="lbd-wn">${e.wins} wins</div><div class="lbd-pt">${formatPoints(e.points)}</div></div>`,
    )
    .join('');
  return `<div class="lbd-body"><div class="lbd-deco">${deco}</div><div class="lbd-podium">${col(2)}${col(1)}${col(3)}</div><div class="lbd-base"></div><div class="lbd-trows">${rows}</div></div>`;
}

/** One screen, drawn complete: header, badge, the screen's content, and the footer with its page dots. */
export function screenHtml(m: DisplayModel, screens: DisplayScreen[], index: number): string {
  const screen = screens[index] ?? screens[0] ?? { kind: 'table' as const, page: 0 };
  let body: string;
  let right = '';
  if (!m.board) {
    body = '<div class="lbd-body"><p class="lbd-empty">There is no leaderboard season to show right now.</p></div>';
  } else if (screen.kind === 'podium') {
    body = podiumBody(m);
    right = footerRight(m.board, screen);
  } else {
    body = tableBody(m, screen.page);
    right = footerRight(m.board, screen);
  }
  const dots =
    screens.length > 1
      ? screens.map((_, i) => `<button type="button" class="lbd-dot${i === index ? ' lbd-on' : ''}" data-i="${i}" aria-label="Screen ${i + 1} of ${screens.length}"></button>`).join('')
      : '';
  const left = m.board ? liveHtml(m.board, m.generatedAt) : '';
  return `<div class="lbd-fade lbd-frame">${headerHtml(m)}${stripHtml(m)}${body}</div>
    <div class="lbd-ftr"><span class="lbd-live" id="lbdLive">${left}</span><span class="lbd-mid">${dots}</span><span>${esc(right)}</span>${m.view === 'tv' ? '<div class="lbd-bar" id="lbdBar"></div>' : ''}</div>`;
}

// --- the venue TV ---------------------------------------------------------------------------------------------------------

const POLL_MS = 30_000;
const TICK_MS = 250;
const RELOAD_MS = 6 * 60 * 60 * 1000;

/** Opens the TV board for a TV link's token: no activation, no controls — it polls, rotates and keeps the screen awake by itself. */
export function mountTvDisplay(root: HTMLElement, token: string): void {
  document.body.classList.add('lbd-tv-body');
  root.innerHTML = `<div class="lbd-tvroot">${updateMarker('idle')}<div class="lbd-box" id="lbdBox"><div class="lbd-stage lbd-tv" id="lbdStage"><p class="lbd-empty">Connecting…</p></div></div></div>`;
  const box = root.querySelector<HTMLElement>('#lbdBox')!;
  const stage = root.querySelector<HTMLElement>('#lbdStage')!;

  let reply: Extract<TvReply, { ok: true }> | undefined;
  let signature = '';
  let index = 0;
  let elapsed = 0;
  let stopped = false;

  const fit = () => {
    const s = Math.min(innerWidth / 1920, innerHeight / 1080);
    stage.style.transform = `scale(${s})`;
    box.style.width = `${1920 * s}px`;
    box.style.height = `${1080 * s}px`;
  };
  const screens = (): DisplayScreen[] => (reply ? displayScreens(reply.board?.entries.length ?? 0, reply.display) : []);

  const paint = () => {
    if (!reply) return;
    const list = screens();
    if (index >= list.length) index = 0;
    const model: DisplayModel = {
      view: 'tv',
      board: reply.board,
      branding: reply.branding ?? readBranding(),
      settings: reply.display,
      generatedAt: reply.generatedAt,
      now: Date.now(),
    };
    stage.innerHTML = screenHtml(model, list, index);
    stage.querySelectorAll<HTMLButtonElement>('.lbd-dot').forEach((d) => d.addEventListener('click', () => go(Number(d.dataset.i))));
    elapsed = 0;
  };
  const go = (i: number) => {
    const n = screens().length;
    if (n === 0) return;
    index = (i + n) % n;
    paint();
  };

  const showMessage = (text: string) => {
    stopped = true;
    stage.innerHTML = `<p class="lbd-empty">${esc(text)}</p>`;
  };

  const load = async () => {
    const r = await fetchTvBoard(token);
    if (!r.ok) {
      if (r.reason === 'bad_link') showMessage('This display link is not valid any more. Ask the staff for a new one.');
      else if (!reply) stage.innerHTML = '<p class="lbd-empty">Waiting for the connection…</p>';
      return;
    }
    const changed = JSON.stringify([r.board, r.display, r.branding]) !== signature;
    reply = r;
    if (r.display.theme !== undefined) applyTheme(r.display.theme ?? 'elevated');
    if (changed) {
      signature = JSON.stringify([r.board, r.display, r.branding]);
      paint();
    } else {
      const live = stage.querySelector<HTMLElement>('#lbdLive');
      if (live && r.board) live.innerHTML = liveHtml(r.board, r.generatedAt);
    }
  };

  fit();
  addEventListener('resize', fit);
  void load();
  setInterval(() => {
    if (!stopped) void load();
  }, POLL_MS);
  addEventListener('online', () => void load());

  // Rotation: every screen for the owner's/staff's chosen number of seconds, with a thin progress bar along the bottom.
  setInterval(() => {
    if (stopped || !reply) return;
    const n = screens().length;
    const ms = reply.display.screenSeconds * 1000;
    elapsed += TICK_MS;
    if (n > 1 && elapsed >= ms) return go(index + 1);
    const bar = stage.querySelector<HTMLElement>('#lbdBar');
    if (bar) bar.style.width = n > 1 ? `${Math.min(100, (elapsed / ms) * 100)}%` : '0';
  }, TICK_MS);

  // A TV must not go to sleep; the lock is dropped when the tab is hidden, so ask again each time it is shown.
  const keepAwake = () => {
    const wl = (navigator as Navigator & { wakeLock?: { request(type: 'screen'): Promise<unknown> } }).wakeLock;
    void wl?.request('screen').catch(() => undefined);
  };
  keepAwake();
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') keepAwake();
  });
  // One tap or click goes full screen (or back); the arrow keys step through the screens (a remote's left/right does too).
  root.addEventListener('click', () => {
    if (document.fullscreenElement) void document.exitFullscreen();
    else void document.documentElement.requestFullscreen?.().catch(() => undefined);
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowRight') go(index + 1);
    if (e.key === 'ArrowLeft') go(index - 1);
  });
  // A page left up for days would never get a new version of the app, so it reloads itself now and then.
  setTimeout(() => location.reload(), RELOAD_MS);
}

// --- the player's phone ---------------------------------------------------------------------------------------------------

export interface WebBoardActions {
  back(): void;
  refresh(): void;
}

/**
 * The board on a player's phone: the same screens, one at a time — swipe, tap the dots, or use the arrows. It always uses THIS
 * device's own colours (the 🎨 button opens the usual picker); the theme the owner sets in Admin is for the TV page only.
 */
export function renderWebBoard(
  root: HTMLElement,
  data: { board: DisplayBoard | null; settings: DisplaySettings; generatedAt: number },
  highlight: string[],
  actions: WebBoardActions,
): void {
  const model: DisplayModel = {
    view: 'web',
    board: data.board,
    branding: readBranding(),
    settings: data.settings,
    generatedAt: data.generatedAt,
    now: Date.now(),
    highlight,
  };
  const screens = displayScreens(data.board?.entries.length ?? 0, data.settings);
  root.innerHTML = `
    <div class="lbd-stage lbd-web" id="lbdStage"></div>
    <div class="row" style="margin-top:0.75rem">
      <button type="button" id="lbBoardBackBtn" class="secondary">← Back</button>
      <button type="button" id="lbBoardRefreshBtn">Refresh</button>
      <button type="button" class="theme-picker-btn" aria-label="Choose a look and text size" title="Look and text size">🎨</button>
    </div>`;
  const stage = root.querySelector<HTMLElement>('#lbdStage')!;
  let index = 0;
  const paint = () => {
    stage.innerHTML = screenHtml(model, screens, index);
    stage.querySelectorAll<HTMLButtonElement>('.lbd-dot').forEach((d) => d.addEventListener('click', () => go(Number(d.dataset.i))));
  };
  const go = (i: number) => {
    index = (i + screens.length) % screens.length;
    paint();
  };
  paint();

  let startX = 0;
  let startY = 0;
  stage.addEventListener('pointerdown', (e) => {
    startX = e.clientX;
    startY = e.clientY;
  });
  stage.addEventListener('pointerup', (e) => {
    const dx = e.clientX - startX;
    const dy = e.clientY - startY;
    if (screens.length > 1 && Math.abs(dx) > 50 && Math.abs(dx) > 2 * Math.abs(dy)) go(index + (dx < 0 ? 1 : -1));
  });
  root.querySelector<HTMLButtonElement>('#lbBoardBackBtn')!.addEventListener('click', () => actions.back());
  root.querySelector<HTMLButtonElement>('#lbBoardRefreshBtn')!.addEventListener('click', () => actions.refresh());
}
